"use client";

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import {
  thumbSrc as ikThumbSrc,
  thumbSrcSet as ikThumbSrcSet,
} from "./thumbnails";
import useRevealOnScroll from "./useRevealOnScroll";
import { AI_MINDSET } from "./cairaCourses";
import { EDGE_LEFT, EDGE_RIGHT, EDGE_TAIL } from "./pageWidth";

type Course = {
  id: string;
  /** The card heading, straight from the sheet. Also used for the image alt. */
  title: string;
  /** Topic label under the title. Per-card — see the note above `courses`. */
  category: string;
  /** Bare ImageKit path — the transform is appended at render, see thumbSrc. */
  thumbnail: string;
  videoUrl: string;
};

const IK = AI_MINDSET.ik;

const thumbSrc = (path: string, width: number) => ikThumbSrc(IK, path, width);
const thumbSrcSet = (path: string) => ikThumbSrcSet(IK, path);

/* Two screens' worth up front; the rest arrive as the track is scrolled.
   See ./useRevealOnScroll. */
const INITIAL_VISIBLE = 6;
const REVEAL_BATCH = 4;

/**
 * Sizing for these lives in ./thumbnails — one ladder shared by all four
 * carousels, so the widths and quality can be tuned in a single place.
 */
const courses: Course[] = AI_MINDSET.courses;

/** Accepts youtu.be/<id>, /watch?v=<id> or /embed/<id> — share params are ignored. */
const getYouTubeId = (url: string): string => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/))([\w-]{11})/,
  );
  return match ? match[1] : "";
};

const PlayGlyph = ({ className = "h-3 w-3" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const CloseGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className="h-4 w-4"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Mounted only once a card is clicked, so no YouTube JS or iframe on load. */
const VideoModal = ({
  course,
  onClose,
}: {
  course: Course;
  onClose: () => void;
}) => {
  const [mounted, setMounted] = useState(false);

  useModalScrollLock(true, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!mounted) return null;

  // Portalled to <body> so the overlay can't be trapped by an ancestor
  // stacking context.
  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${course.title} trailer`}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-w-[900px]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-10 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-white/60 text-white hover:border-white"
        >
          <CloseGlyph />
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${getYouTubeId(
              course.videoUrl,
            )}?autoplay=1&rel=0`}
            title={course.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CourseCard = ({
  course,
  onPlay,
}: {
  course: Course;
  onPlay: (_course: Course) => void;
}) => (
  <div className="flex h-full flex-col">
    <button
      type="button"
      onClick={() => onPlay(course)}
      aria-label={`Play trailer — ${course.title}`}
      className="group relative block aspect-video w-full overflow-hidden rounded-lg"
    >
      {/* The masters are all exactly 16:9, so 1280x720 as the intrinsic ratio
          matches the aspect-video box and nothing shifts as they decode. */}
      <img
        src={thumbSrc(course.thumbnail, 640)}
        srcSet={thumbSrcSet(course.thumbnail)}
        sizes="(max-width: 768px) 80vw, 31vw"
        alt={course.title}
        width={1280}
        height={720}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-[#0F0F0F]">
          <PlayGlyph className="ml-1 h-5 w-5" />
        </span>
      </span>
    </button>

    <p className="mt-3 line-clamp-2 min-h-[42px] text-[13px] leading-relaxed text-white">
      {course.title}
    </p>

    <p className="mt-1 text-[13px] text-[#9A9A9A]">{course.category}</p>

    <button
      type="button"
      onClick={() => onPlay(course)}
      className="mt-3 flex w-fit items-center gap-2 rounded-md bg-white px-3 py-1.5 text-[13px] font-medium text-[#141414] transition-colors hover:bg-[#E6E6E6]"
    >
      <PlayGlyph />
      Trailer
    </button>
  </div>
);

const AiReadiness: React.FC = () => {
  const [activeCourse, setActiveCourse] = useState<Course | null>(null);

  const { trackRef, visibleCount } = useRevealOnScroll(courses.length, {
    initial: INITIAL_VISIBLE,
    batch: REVEAL_BATCH,
  });

  const closeModal = useCallback(() => setActiveCourse(null), []);

  return (
    <section className="w-full bg-black py-6 md:pt-[50px] pb-[25px]">
      <div
        className={`flex items-start justify-between gap-4 ${EDGE_LEFT} ${EDGE_RIGHT}`}
      >
        <div>
          <h2 className="text-[15px] font-bold text-white md:text-2xl">
            AI Mindset, Skillset & Toolset
          </h2>
          <p className="mt-1 line-clamp-1 text-[13px] text-[#9A9A9A] md:line-clamp-none md:text-sm">
            Building AI Agents & Workflows on the Microsoft Stack (Copilot,
            Power Automate, Copilot Studio, Power BI)
          </p>
        </div>
        {/* <span className="shrink-0 pt-1 text-white">
          <TuneGlyph />
        </span> */}
      </div>

      {/*
        A plain overflow-x scroller: horizontal trackpad swipes, touch swipes
        and arrow keys are all handled by the browser, and a vertical gesture is
        left alone so the page scrolls as normal. On mobile it bleeds off the
        right edge the way the design shows; on desktop EDGE_TAIL closes it with
        the same gutter as the left, so the last card isn't flush to the edge.
        The left padding is what sets the first card's inset, and it's the same
        EDGE_LEFT the heading uses so the two always line up. scroll-padding
        matches it too, otherwise snap-start would pin every card after the
        first to 0 and the inset would vanish as soon as you swipe.
      */}
      <div
        ref={trackRef}
        className={`scrollbar-hide mt-5 flex snap-x snap-mandatory items-stretch overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-pl-4 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#2A85FF] sm:scroll-pl-6 md:snap-none ${EDGE_LEFT} ${EDGE_TAIL}`}
        // A scrollable region has to be reachable by keyboard for the arrow
        // keys to have anywhere to land — the rule doesn't model that case.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label="AI readiness courses — scroll sideways to browse"
      >
        {courses.slice(0, visibleCount).map((course) => (
          <div
            key={course.id}
            /* md:last:mr-0 so the trailing gap doesn't stack on EDGE_TAIL. */
            className="mr-3.5 w-[80%] shrink-0 snap-start md:w-[30.6%] md:last:mr-0"
          >
            <CourseCard course={course} onPlay={setActiveCourse} />
          </div>
        ))}
      </div>

      {activeCourse && (
        <VideoModal course={activeCourse} onClose={closeModal} />
      )}
    </section>
  );
};

export default AiReadiness;
